import { getValueOrFunction } from "@base/util/TypeUtil";
import { Unit } from "@biz/Definitions";
import { getPylonArticleId, IArticleAffiliation, IShoppingArticle, RAFF_PREMONTAGE } from "@biz/definitions/ArticleDefinitions";
import { MaterialType, PylonType, pylonTypes } from "@biz/definitions/SunSailDefinitions";
import { ArticleLogic } from "@biz/logic/ArticleLogic";
import { Measure } from "@biz/logic/Measure";
import { IArgServer, IInfoItem, IOrderArticleInfo, IOrderFormItemOptions, IPartListArticleItem, IPartListProductContext } from "@biz/models/PartListDefinitions";
import { PartListHelper } from "@biz/models/PartListHelper";
import { ShoppingCartPriority } from "@biz/models/ShoppingListDefinitions";

type MotorType = "c12r40" | "e12r40" | "c12r40+";

export interface IOrderFormManager {
  addInfo(infoItems: IInfoItem[], name: string, value: string): void;
  addItem(stockList: IPartListArticleItem[], inputArgs: IArgServer, affiliation: IArticleAffiliation | undefined, count: number | undefined, _options?: Partial<IOrderFormItemOptions>): void;
  addRaff(productContext: IPartListProductContext, inputArgs: IArgServer, article: IShoppingArticle): any;
  addBaseCubeConcrete(productContext: IPartListProductContext, inputArgs: IArgServer, poleIndex: number, count: number): void;
}

export type RequireOne<T, K extends keyof T> = {
  [X in Exclude<keyof T, K>]: T[X];
} &
  {
    [P in K]-?: T[P];
  };

export type AnnotationArticle<T extends keyof IOrderArticleInfo> = RequireOne<IOrderArticleInfo, T>;

export class ArticleInfo {
  private static ofm: IOrderFormManager;

  private static articles: Record<string, IOrderArticleInfo> | undefined;
  private static coveringCapArticles: RequireOne<IOrderArticleInfo, "coveringCap">[] | undefined;

  private static createPylon(materialType: MaterialType, length: number, pylonType: PylonType): { id: string, info: Partial<IOrderArticleInfo> } {
    const id = getPylonArticleId(materialType, length, pylonType);
    return {
      id,
      info: { /*partListName: "Pylon " + materialType + " " + length + " " + pylonType,*/ defaultPartList: "none" }, // https://plaspack.atlassian.net/jira/software/projects/APP/issues/APP-1249
    }
  }

  private static createPylons(): Record<string, Partial<IOrderArticleInfo>> {
    const values: { id: string, info: Partial<IOrderArticleInfo> }[] = [];
    for (const m of ["Droppole", "Droppole_Black"] satisfies MaterialType[]) {
      for (const l of [3, 4, 5, 6]) {
        for (const pt of pylonTypes) {
          values.push(this.createPylon(m, l, pt));
        }
      }
    }

    for (const m of ["Alu", "Alu_Black", "Steel", "Steel_V4A"] satisfies MaterialType[]) {
      for (const l of [3, 6]) {
        for (const pt of pylonTypes) {
          values.push(this.createPylon(m, l, pt));
        }
      }
    }

    const ret: Record<string, Partial<IOrderArticleInfo>> = {};
    for (const v of values)
      ret[v.id] = v.info;
    return ret;
  }

  private static createArticles(): Record<string, Partial<IOrderArticleInfo>> {
    return {
      ...ArticleInfo.createPylons(),
      // Fertigungskosten
      AZ000011: { partListName: "Fertigungskosten: {mul2}€" },

      // Umlenkset
      "71007A001": { defaultPartList: "none" },

      // Verspannungsset Wand/Wand
      "00E13000114": { partListName: "Verspannungsset Wand/Wand + {twistingSetRope}lfm Drahtseil" },

      // Verspannungsset Wand/Mast
      "00E13000115": { partListName: "Verspannungsset Wand/Mast ({twistingSetText}) + {twistingSetRope}lfm Drahtseil" },

      // Verspannungsset Mast/Mast
      "00E13000116": { partListName: "Verspannungsset Mast/Mast ({twistingSetText}) + {twistingSetRope}lfm Drahtseil" },

      "00E13000067": { beosInfo: { anzahlCalculationType: "laufMeter" }, stkUnit: "Stk", mulUnit: "m" }, // Drahtseil 4mm aus Edelstahl
      "00E13000255": { beosInfo: { anzahlCalculationType: "laufMeter" }, stkUnit: "Stk", mulUnit: "m" }, // Drahtseil aus Edelstahl

      "00E13000282": { beosInfo: { anzahlCalculationType: "laufMeter" }, stkUnit: "Stk", mulUnit: "m" }, // Kederschiene für 7,5mm
      "00E13000684": { beosInfo: { anzahlCalculationType: "laufMeter" }, stkUnit: "Stk", mulUnit: "m" }, // Kederschiene für 10-13mm

      // Raff
      "75001C": {
        type: "includedOnly",
        callback: (params) => ArticleInfo.ofm.addRaff(params.args, params.inputArgs, params.article),
      },
      "75001S": {
        type: "includedOnly",
        callback: (params) => ArticleInfo.ofm.addRaff(params.args, params.inputArgs, params.article),
      },
      "75002S": {
        type: "includedOnly",
        callback: (params) => ArticleInfo.ofm.addRaff(params.args, params.inputArgs, params.article),
      },
      "75001M": {
        type: "includedOnly",
        callback: (params) => ArticleInfo.ofm.addRaff(params.args, params.inputArgs, params.article),
      },

      // Profilzuschnitt
      "00E13001123": {
        partListName: `Trageprofil für Motoreinheit: {mul1000}${Measure.mm}`, beosInfo: { anzahlCalculationType: "laufMeter" },
      },
      "00E13001124": { partListName: `Antriebswelle: {mul1000}${Measure.mm}`, beosInfo: { anzahlCalculationType: "laufMeter" } },
      "00E13001308": { partListName: `Führungsschiene Raff-C: {mul1000}${Measure.mm}`, beosInfo: { anzahlCalculationType: "laufMeter" } },
      "00E13001118": { partListName: `Tuchprofil TPK35: {mul1000}${Measure.mm}` },
      "00E13001102": { partListName: `Tuchprofil TP8: {mul1000}${Measure.mm}` },

      // Führungsschiene Raff-C mit Antrieb
      "00E13001143": { partListName: "Zahnriemen: {mul2}m" },
      // Zubehör Raincut
      "00EK4916023": { mulUnit: "m" },

      // Raff M
      "00E13001129": { partListName: `Führungsschiene f. manuelle Systeme Raff&Vorhang: {mul1000}${Measure.mm}`, beosInfo: { anzahlCalculationType: "laufMeter" } },
      "00E13000600": {
        defaultPartList: "hardwareList",
        orderGroup: "Vorgefertigt",
      },
      "60AZUBS038": {
        defaultPartList: "hardwareList",
        orderGroup: "Vorgefertigt",
      },
      "00E13000324": {
        // defaultPartList: "colliDispatch",
        // colli: { diag: 10, length: 0.77, weight: 9.5, place: 30 },
        defaultPartList: "hardwareList",
        orderGroup: "Vorgefertigt",

        // partListName: "D-07 (Droppole Dorn <mark><b>kurz</b></mark>)", // https://plaspack.atlassian.net/jira/software/projects/APP22/boards/2?selectedIssue=APP22-457 https://plaspack.atlassian.net/jira/software/projects/APP/issues/APP-1249
        callback: (params) => {
          ArticleInfo.ofm.addItem(PartListHelper.getChapterArticles(params.args.chapter.articles, "stockList"), params.inputArgs, params.article.affiliation, params.article.count, { articleId: "00E13001877" }); // 5 x ISK-Schraube M6x25 + Zahnscheibe
        },
      },
      "00E13000381": {
        // defaultPartList: "colliDispatch",
        // colli: { diag: 10, length: 1.21, weight: 16, place: 31 },
        defaultPartList: "hardwareList",
        orderGroup: "Vorgefertigt",
        // partListName: "D-09 (Droppole Dorn <mark><b>lang</b></mark>)", // https://plaspack.atlassian.net/jira/software/projects/APP22/boards/2?selectedIssue=APP22-457 https://plaspack.atlassian.net/jira/software/projects/APP/issues/APP-1249
        callback: (params) => {
          if (params.args.chapter.type !== "sunSail") return;
          ArticleInfo.ofm.addItem(PartListHelper.getChapterArticles(params.args.chapter.articles, "stockList"), params.inputArgs, params.article.affiliation, params.article.count, { articleId: "00E13001877" }); // 5 x ISK-Schraube M6x25 + Zahnscheibe
          // ArticleInfo.ofm.addItem(args.sunSail.stockList, inputArgs, article.keys.poleId, article.count * 5, {
          //   name: "ISK-Schraube M6x25 + Zahnscheibe",
          // });
        },
      },
      "00E13000756": {
        // defaultPartList: "colliDispatch",
        // colli: { diag: 16, length: 1, weight: 9, place: 32 },
        defaultPartList: "hardwareList",
      },
      // Basecube
      "00E13000301": {
        defaultPartList: "hardwareList",
        partListName: `Basecube Grundgestell inkl. Bodengitter inkl. {count}*4 Stk. M10x30 + BLS für Mastmontage + Mastschellen({poleMaterialType})`,
        dontMerge: true,
      }, // 5.3.2019
      "00E13000302": {
        defaultPartList: "hardwareList",
        partListName: "Basecube Grundgestell inkl. Bodengitter (10°)",
        callback: (params) => {
          const chapter = params.args.chapter;
          if (chapter.type !== "sunSail") {
            console.warn("Not a sunsail chapter!");
            return;
          }

          const hardwareItem = PartListHelper.getHardwarePole(chapter, params.article.affiliation!.poleId!);
          hardwareItem.poleMountDrill.push("10° Dorn<br/>Base Cube");
          ArticleInfo.ofm.addItem(PartListHelper.getChapterArticles(chapter.articles, "hardwareList"), params.inputArgs, params.article.affiliation, params.article.count, {
            name: "gebogener Dorn 10°",
          });
          ArticleInfo.ofm.addItem(PartListHelper.getChapterArticles(chapter.articles, "hardwareList"), params.inputArgs, params.article.affiliation, params.article.count * 2, {
            name: `verzinkte Schellen für Dorn + {count}*4 Stk M10x30 + BLS für Montage in Base Cube  + Mastschellen({poleMaterialType})`,
            dontMerge: true,
          });
          ArticleInfo.ofm.addItem(PartListHelper.getChapterArticles(chapter.articles, "stockList"), params.inputArgs, params.article.affiliation, params.article.count, { articleId: "00E13001877" }); // 5 x ISK-Schraube M6x25 + Zahnscheibe
        },
      },
      "00E13000303": {
        defaultPartList: "hardwareList",
        partListName: "Basecube Grundgestell inkl. Bodengitter (0°)",
        callback: (params) => {
          const chapter = params.args.chapter;
          if (chapter.type !== "sunSail") {
            console.warn("Not a sunsail chapter!");
            return;
          }

          const hardwareItem = PartListHelper.getHardwarePole(chapter, params.article.affiliation!.poleId!);
          hardwareItem.poleMountDrill.push("0° Dorn<br/>Base Cube");
          ArticleInfo.ofm.addItem(PartListHelper.getChapterArticles(chapter.articles, "hardwareList"), params.inputArgs, params.article.affiliation, params.article.count, {
            name: "Dorn 0°",
          });
          ArticleInfo.ofm.addItem(PartListHelper.getChapterArticles(chapter.articles, "hardwareList"), params.inputArgs, params.article.affiliation, params.article.count * 2, {
            name: `verzinkte Schellen für Dorn + {count}*4 Stk M10x30 + BLS für Montage in Base Cube  + Mastschellen({poleMaterialType})`,
            dontMerge: true,
          });
          ArticleInfo.ofm.addItem(PartListHelper.getChapterArticles(chapter.articles, "stockList"), params.inputArgs, params.article.affiliation, params.article.count, { articleId: "00E13001877" }); // 5 x ISK-Schraube M6x25 + Zahnscheibe
        },
      },
      "00E13001309": {
        defaultPartList: "hardwareList",
        callback: (params) => {
          ArticleInfo.ofm.addBaseCubeConcrete(params.args, params.inputArgs, params.article.affiliation!.poleId!, params.article.count);
        },
      },
      "00E13001366": {
        defaultPartList: "hardwareList",
        callback: (params) => {
          ArticleInfo.ofm.addBaseCubeConcrete(params.args, params.inputArgs, params.article.affiliation!.poleId!, params.article.count);
        },
      },
      "00E13000323": {
        // partListName: "Bodenplatte mit massivem Dorn 10° <mark><b>für Droppole</b></mark>",
        callback: (params) => {
          ArticleInfo.ofm.addItem(PartListHelper.getChapterArticles(params.args.chapter.articles, "stockList"), params.inputArgs, params.article.affiliation, params.article.count, { articleId: "00E13001877" }); // 5 x ISK-Schraube M6x25 + Zahnscheibe
          /*ArticleInfo.ofm.addItem(args.sunSail.stockList, inputArgs, article.keys.poleId, article.count * 5, {
            name: "M6x30 Imbuss inkl. Zahnscheiben",
          });*/
        },
      },
      "00E13000325": {
        // partListName: "Bodenplatte mit massivem Dorn 2° <mark><b>für Droppole</b></mark>",
        callback: (params) => {
          ArticleInfo.ofm.addItem(PartListHelper.getChapterArticles(params.args.chapter.articles, "stockList"), params.inputArgs, params.article.affiliation, params.article.count, { articleId: "00E13001877" }); // 5 x ISK-Schraube M6x25 + Zahnscheibe
          // ArticleInfo.ofm.addItem(args.sunSail.stockList, inputArgs, article.keys.poleId, article.count * 5, {
          //   name: "M6x30 Imbuss inkl. Zahnscheiben",
          // });
        },
      },
      "00E13000102": {
        defaultPartList: "none",
      },
      "00E13001782": {
        defaultPartList: "none",
      },
      "00E13000970": { partListName: "Umlaufrolle Custom Design" },

      // Snap:
      "00E13001324": { partListName: "Seil 8 mm sehr griffig; Farbe: grau/farbig lt. Farbkarte" },
      "00E13001083": { partListName: "Snap Komplettsett (Masten)" }, // deprecated
      "00E13000817": { partListName: "Snap Komplettsett (Wand)" }, // deprecated
      "00E13001470": { partListName: "Snap-Grundplatte mit Fallstopp-Klemme f. {poleMaterialName}" }, // deprecated
      "00E13000984": { partListName: "Edelstahl Öse im Mast" },

      // Amplifier
      "00E13000131": {
        defaultPartList: "hardwareList",
        // partListName: "Versteifung für Alu Masten",
        // hardwareItem: { poleAmplifier: true },
      },
      "00E13000096": {
        defaultPartList: "hardwareList",
        // partListName: "Versteifung eingeschweißt für Edelstahl Masten <mark><b>aus Stahl</b></mark>",
        // hardwareItem: { poleAmplifier: true },
      },
      "00E13001645": {
        defaultPartList: "hardwareList",
        // partListName: "Versteifung eingeschweißt für Edelstahl Masten <mark><b>aus Edelstahl</b></mark>",
        // hardwareItem: { poleAmplifier: true },
      },

      "00E13000987": {
        partListName: "Manuelles Höhenverstellungsset Design <mark><b>(Titan)</b></mark> inkl. Kurbel für Droppole",
      },
      "00E13000989": {
        partListName: "Manuelles Höhenverstellungsset Design <mark><b>(Blau)</b></mark> inkl. Stk.</b> Kurbel für Droppole",
      },
      // Dyremaseil 4mm
      "00E13000107": { defaultPartList: (version) => !version ? "none" : "stockList", stkUnit: "Stk", mulUnit: "m", beosInfo: { anzahlCalculationType: "laufMeter" } },

      // Biegung
      "00E13000133": { defaultPartList: "none" },
      // Biegung eines Edelstahlmastes 0°-20°
      "00E13000105": { defaultPartList: "none" },
      // Biegung eines Edelstahlmastes 20°-40°
      "00E13001633": { defaultPartList: "none" },
      "00E13000160": { defaultPartList: "none" },
      // Biegung vom Edelstahlmast mit Versteifungsrohr, 0°-20°
      "00E13000739": { defaultPartList: "none" },
      // Biegung vom Edelstahlmast mit Versteifungsrohr, 20°-40°
      "00E13001634": { defaultPartList: "none" },

      // Zuschnitt auf Maß DP
      "00E13001135": { defaultPartList: "sunSailList" },
      // spannseil 8.0 grau E (enthalten)
      "00E13001404": {
        defaultPartList: "sunSailList",
        stkUnit: "", // m => shows 1m x 0€ in App
        mulUnit: "m",
        rope: { ropeType: "8E", diameter: 8, colorId: "grey" },
      },
      // spannseil 8.0 Hellblau E
      "00E13001398": {
        defaultPartList: "sunSailList",
        stkUnit: "", // m => shows 1m x 0€ in App
        mulUnit: "m",
        rope: { ropeType: "8E", diameter: 8, colorId: "blue" },
      },
      // spannseil 8.0 Dunkelblau E
      "00E13001399": {
        defaultPartList: "sunSailList",
        stkUnit: "", // m => shows 1m x 0€ in App
        mulUnit: "m",
        rope: { ropeType: "8E", diameter: 8, colorId: "navy" },
      },
      // spannseil 8.0 Violett E
      "00E13001400": {
        defaultPartList: "sunSailList",
        stkUnit: "", // m => shows 1m x 0€ in App
        mulUnit: "m",
        rope: { ropeType: "8E", diameter: 8, colorId: "purple" },
      },
      // spannseil 8.0 Orange E
      "00E13001401": {
        defaultPartList: "sunSailList",
        stkUnit: "", // m => shows 1m x 0€ in App
        mulUnit: "m",
        rope: { ropeType: "8E", diameter: 8, colorId: "orange" },
      },
      // spannseil 8.0 Rot E
      "00E13001402": {
        defaultPartList: "sunSailList",
        stkUnit: "", // m => shows 1m x 0€ in App
        mulUnit: "m",
        rope: { ropeType: "8E", diameter: 8, colorId: "red" },
      },
      // spannseil 8.0 Grün E
      "00E13001403": {
        defaultPartList: "sunSailList",
        stkUnit: "", // m => shows 1m x 0€ in App
        mulUnit: "m",
        rope: { ropeType: "8E", diameter: 8, colorId: "green" },
      },
      // spannseil 8 schwarz/orange E
      "00E13002072": {
        defaultPartList: "sunSailList",
        stkUnit: "",
        mulUnit: "m",
        rope: { ropeType: "8E", diameter: 8, colorId: "black" },
      },
      // Schutzhülle Soliday M
      "70004A001": {
        defaultPartList: "sunSailList",
        partListNameOldApp: "<b>{length} lfm</b>{protectiveCoverLength} Abdeckhülle für System M <mark><b>Farbe: Stone</b></mark>",
        beosInfo: { protectiveCover: true, anzahlCalculationType: "laufMeter" },
        stkUnit: "Stk",
        mulUnit: "m",
        hasLength: true,
      }, // unit "lfm" => PartList 1lfm 5lfm ...
      // Schutzhülle Soliday M
      "70004A002": {
        defaultPartList: "sunSailList",
        partListNameOldApp: "<b>{length} lfm</b>{protectiveCoverLength} Abdeckhülle für System M <mark><b>Farbe: {colorId}</b></mark>",
        beosInfo: { protectiveCover: true, anzahlCalculationType: "laufMeter" },
        stkUnit: "Stk",
        mulUnit: "m",
        hasLength: true,
      },
      // Schutzhülle Soliday M
      "70004A003": {
        defaultPartList: "sunSailList",
        partListNameOldApp: "<b>{length} lfm</b>{protectiveCoverLength} Abdeckhülle <mark><b>Premium</b></mark> für System M <mark><b>Farbe: Stone</b></mark>",
        beosInfo: { protectiveCover: true, anzahlCalculationType: "laufMeter" },
        stkUnit: "Stk",
        mulUnit: "m",
        hasLength: true,
      },
      // Schutzhülle Soliday M
      "70004A004": {
        defaultPartList: "sunSailList",
        partListNameOldApp: "<b>{length} lfm</b>{protectiveCoverLength} Abdeckhülle <mark><b>Premium</b></mark> für System M <mark><b>Farbe: {colorId}</b></mark>",
        beosInfo: { protectiveCover: true, anzahlCalculationType: "laufMeter" },
        stkUnit: "Stk",
        mulUnit: "m",
        hasLength: true,
      },
      // Schutzhülle
      "73004A007": {
        defaultPartList: "sunSailList",
        partListNameOldApp: "<b>{length} lfm</b>{protectiveCoverLength} Abdeckhülle <mark><b>Premium</b></mark> für System A/M/CS <mark><b>Farbe: Stone</b></mark>",
        beosInfo: { protectiveCover: true, anzahlCalculationType: "laufMeter" },
        stkUnit: "Stk",
        mulUnit: "m",
        hasLength: true,
      },
      // Schutzhülle
      "73004A008": {
        defaultPartList: "sunSailList",
        partListNameOldApp: "<b>{length} lfm</b>{protectiveCoverLength} Abdeckhülle <mark><b>Premium</b></mark> für System A/M/CS <mark><b>Farbe: {colorId}</b></mark>",
        beosInfo: { protectiveCover: true, anzahlCalculationType: "laufMeter" },
        stkUnit: "Stk",
        mulUnit: "m",
        hasLength: true,
      },
      // Schutzhülle Soliday CS
      "72004A001": {
        defaultPartList: "sunSailList",
        partListNameOldApp: "<b>{length} lfm</b>{protectiveCoverLength} Abdeckhülle für System CS <mark><b>Farbe: Stone</b></mark>",
        beosInfo: { protectiveCover: true, anzahlCalculationType: "laufMeter" },
        stkUnit: "Stk",
        mulUnit: "m",
        hasLength: true,
      },
      // Schutzhülle Soliday CS
      "72004A002": {
        defaultPartList: "sunSailList",
        partListNameOldApp: "<b>{length} lfm</b>{protectiveCoverLength} Abdeckhülle für System CS <mark><b>Farbe: {colorId}</b></mark>",
        beosInfo: { protectiveCover: true, anzahlCalculationType: "laufMeter" },
        stkUnit: "Stk",
        mulUnit: "m",
        hasLength: true,
      },
      // Schutzhülle Soliday CS
      "72004A003": {
        defaultPartList: "sunSailList",
        partListNameOldApp: "<b>{length} lfm</b>{protectiveCoverLength} Abdeckhülle <mark><b>Premium</b></mark> für System CS <mark><b>Farbe: Stone</b></mark>",
        beosInfo: { protectiveCover: true, anzahlCalculationType: "laufMeter" },
        stkUnit: "Stk",
        mulUnit: "m",
        hasLength: true,
      },
      // Schutzhülle Soliday CS
      "72004A004": {
        defaultPartList: "sunSailList",
        partListNameOldApp: "<b>{length} lfm</b>{protectiveCoverLength} Abdeckhülle <mark><b>Premium</b></mark> für System CS <mark><b>Farbe: {colorId}</b></mark>",
        beosInfo: { protectiveCover: true, anzahlCalculationType: "laufMeter" },
        stkUnit: "Stk",
        mulUnit: "m",
        hasLength: true,
      },

      // hwList ?????
      // Hohlsaum 1m
      "00E13001572": { defaultPartList: "sunSailList", stkUnit: "lfm" },
      // Aufpreis f. 1 Ecke mit Segelplatte groß
      "00E13001416": { defaultPartList: "sunSailList" },
      // Eckenauführung ChillSail
      "90SOL0102": { defaultPartList: "sunSailList", beosInfo: { position: "K" } },
      // Eckenauführung D-Ring
      "90SOL0101": { defaultPartList: "sunSailList", beosInfo: { position: "K" } },
      "00E13001637": { defaultPartList: "sunSailList", beosInfo: { position: "K" } }, // Design Ring 25
      "00E13001638": { defaultPartList: "sunSailList", beosInfo: { position: "K" } }, // Design Ring 40
      // Aufpreis f. 1 Ecke mit O-Ring
      "00E13001417": { defaultPartList: "sunSailList" },
      // Orca-Designring
      "00E13001951": { defaultPartList: "sunSailList" },
      // Aufpreis f. 1 Ecke mit Rundplatte
      "00E13001418": { defaultPartList: "sunSailList" },
      // Aufpreis f. 1 Ecke mit Orcaplatte
      "00E13001665": { defaultPartList: "sunSailList" },
      // Eckenauführung GB-Schlaufe
      "90SOL0100": { defaultPartList: "sunSailList", beosInfo: { position: "K" } },
      "90SOL0103": { defaultPartList: "sunSailList", beosInfo: { position: "K" } },

      // not used anymore
      // "00E13000097": { defaultPartList: "none", beosInfo: { pole: true } },
      // "00E13000094": { defaultPartList: "none", beosInfo: { pole: true } }, // V2A
      // "00E13001788": { defaultPartList: "none", beosInfo: { pole: true } }, // V4A
      // "00E13000095": { defaultPartList: "none", beosInfo: { pole: true } }, // V2A
      // "00E13001789": { defaultPartList: "none", beosInfo: { pole: true } }, // V4A
      // "00E13000343": { defaultPartList: "none", beosInfo: { pole: true } },
      // "00E13000349": { defaultPartList: "none", beosInfo: { pole: true } },
      // "00E13000350": { defaultPartList: "none", beosInfo: { pole: true } },
      // "00E13001064": { defaultPartList: "none", beosInfo: { pole: true, position: "K", hide: true } }, // nicht ins Exportfile 26. September 2019

      // Alu Holz 3m Pylon
      "00E13001079": { defaultPartList: "none", beosInfo: { pole: true } },
      // Alu Holz 6m Pylon
      "00E13001080": { defaultPartList: "none", beosInfo: { pole: true } },
      // Alu Black 3m Pylon
      "00E13001666": { defaultPartList: "none", beosInfo: { pole: true } },
      // Alu Black 6m Pylon
      "00E13001667": { defaultPartList: "none", beosInfo: { pole: true } },

      // Alu
      "00E13000235": { defaultPartList: "none", beosInfo: { pole: true } },
      // Alu
      "00E13000238": { defaultPartList: "none", beosInfo: { pole: true } },
      // Alu Wood deprecated App 2.55
      "00E13000981": { defaultPartList: "none", beosInfo: { pole: true } },
      // Alu Wood deprecated App 2.55
      "00E13000982": { defaultPartList: "none", beosInfo: { pole: true } },
      // Alu Black
      "00E13001650": { defaultPartList: "none", beosInfo: { pole: true } },
      // Alu Black
      "00E13001652": { defaultPartList: "none", beosInfo: { pole: true } },
      // Iron89
      "00E13001654": { defaultPartList: "none", beosInfo: { pole: true } },
      // Iron89
      "00E13001655": { defaultPartList: "none", beosInfo: { pole: true } },
      // Iron89_Black
      "00E13001656": { defaultPartList: "none", beosInfo: { pole: true } },
      // Iron89_Black
      "00E13001657": { defaultPartList: "none", beosInfo: { pole: true } },

      // Edelstahl 3m
      "00E13000135": { defaultPartList: "none", beosInfo: { pole: true } }, // V2A
      "00E13001766": { defaultPartList: "none", beosInfo: { pole: true } }, // V4A
      // Edelstahl 6m
      "00E13000138": { defaultPartList: "none", beosInfo: { pole: true } }, // V2A
      "00E13001767": { defaultPartList: "none", beosInfo: { pole: true } }, // V4A

      "00E13000831": { defaultPartList: "none", beosInfo: { pole: true } }, // Masten verzinkt 3 m DM114 114x4,5x6000 m. 1 Bohrung
      "00E13000832": { defaultPartList: "none", beosInfo: { pole: true } }, // Masten verzinkt 6 m DM114 114x4,5x6000 m. 1 Bohrung

      // droppole pylon 3m
      "00E13000340": { defaultPartList: "none", beosInfo: { pole: true } },
      // droppole pylon 4m
      "00E13000341": { defaultPartList: "none", beosInfo: { pole: true } },
      // droppole pylon 5m
      "00E13000342": { defaultPartList: "none", beosInfo: { pole: true } },
      // droppole 3m
      "00E13000335": { defaultPartList: "none", beosInfo: { pole: true } },
      // droppole 4m
      "00E13000336": { defaultPartList: "none", beosInfo: { pole: true } },
      // droppole 5m
      "00E13000337": { defaultPartList: "none", beosInfo: { pole: true } },
      // droppole black 3m
      "00E13000338": { defaultPartList: "none", beosInfo: { pole: true } },
      // droppole black 4m
      "00E13000347": { defaultPartList: "none", beosInfo: { pole: true } },
      // droppole black 5m
      "00E13000348": { defaultPartList: "none", beosInfo: { pole: true } },
      // Irom48 3m
      "60001B001": { defaultPartList: "none", beosInfo: { pole: true } },

      "00E13000826": { partListName: "Cleaning Set 5-teilig" },
      "00E13000212": { partListName: "Fluchtlichtstrahler Design IP 65 (70W)" },
      "00E13000230": { partListName: "Fluchtlichtstrahler Design IP 65 (150W)" },
      "00E13000231": { partListName: "Fluchtlichtstrahler Funkfernbedienung" },

      // deprecated
      "71003A028": { partListName: "Soliday Wandsender 5Kanal AP-WS ohne SO-WI-Sensor" },

      "71002A007": { partListName: "Funk-Wandschalter AP Funk ohne SO-WI-Sens. 1K. (EC411)" },

      // Wandbefestigungsplatten
      // deprecated
      "00E13000029": { partListName: "Wandhalterung – Viereck 150 x 150 x 4 mm" /* + args.wallPlateStrong*/ },
      "00E13000117": { partListName: "Wandhalterung – Rund ø180 mm" /* + args.wallPlateStrong*/ },
      "00E13000086": { partListName: "Wandhalterung 250 x 50 x 6 mm mit Haltevorrichtung" /* + args.wallPlateStrong*/ },
      "00E13000186": { partListName: "Wandhalterung 50 x 250 x 6 mm mit Haltevorrichtung" /* + args.wallPlateStrong*/ },
      "00E13000089": {
        partListName: "Eckwandhalterung für Inneneck (130/130 x 150 x 4 mm)" /* + args.wallPlateStrong*/,
      },
      "00E13000090": {
        partListName: "Eckwandhalterung für Außeneck (130/130 x 150 x 4 mm)" /* + args.wallPlateStrong*/,
      },
      "00E13000109": {
        partListName: "Große Eckwandhalterung für Ausseneck 300/300x150x4mm" /* + args.wallPlateStrong*/,
      },

      // list start
      // **** Warenkorb Segelauswahl ****
      // Segelauswahl --- Allgemein
      // Aufpreis f. Funkmotor C12 XL80 LZ 8 Min.
      "71003A014": {
        partListName: "{as400Text}{additionalMotorText}",
        shoppingCartPriority: ShoppingCartPriority.Motor,
        defaultPartList: "systemList",
      },
      "00E13001314": { // Motor ohne Funk
        partListName: "{as400Text}{additionalMotorText}",
        shoppingCartPriority: ShoppingCartPriority.Motor,
        defaultPartList: "systemList",
      },
      "00E13001988": { // Motor ohne Funk
        partListName: "{as400Text}{additionalMotorText}",
        shoppingCartPriority: ShoppingCartPriority.Motor,
        defaultPartList: "systemList",
      },

      "00E13001882": { // Motor Aufpreis für S110
        partListName: "{as400Text}{additionalMotorText}",
        shoppingCartPriority: ShoppingCartPriority.Motor,
        defaultPartList: "systemList",
      },
      "00E13001883": { // Motor Aufpreis für SF90
        partListName: "{as400Text}{additionalMotorText}",
        shoppingCartPriority: ShoppingCartPriority.Motor,
        defaultPartList: "systemList",
      },
      "00E13001884": { // Motor Aufpreis für SF110
        partListName: "{as400Text}{additionalMotorText}",
        shoppingCartPriority: ShoppingCartPriority.Motor,
        defaultPartList: "systemList",
      },

      // Anschlußleitung
      "00E13000850": { shoppingCartPriority: ShoppingCartPriority.MotorCable, defaultPartList: "systemList" }, // 2m
      "00E13000374": { shoppingCartPriority: ShoppingCartPriority.MotorCable, defaultPartList: "systemList" }, // 5m
      "00E13001091": { shoppingCartPriority: ShoppingCartPriority.MotorCable, defaultPartList: "systemList" }, // 10m
      "00E13001901": { shoppingCartPriority: ShoppingCartPriority.MotorCable, defaultPartList: "systemList" }, // 2m
      "00E13001902": { shoppingCartPriority: ShoppingCartPriority.MotorCable, defaultPartList: "systemList" }, // 5m
      "00E13001903": { shoppingCartPriority: ShoppingCartPriority.MotorCable, defaultPartList: "systemList" }, // 10m
      // Sonnensegeltasche 20
      "00E13000827": {
        defaultPartList: "sunSailList",
        orderGroup: "All-In-One",
      },
      // Sonnensegeltasche 20-45
      "00E13000828": {
        defaultPartList: "sunSailList",
        orderGroup: "All-In-One",
      },
      // Sonnensegeltasche 45-65
      "00E13000829": {
        defaultPartList: "sunSailList",
        orderGroup: "All-In-One",
      },

      // Segelauswahl - Abdeckhülle
      // Schutzhülle
      "71004A001": { // Ø84
        defaultPartList: "sunSailList",
        partListNameOldApp: "<b>{length} lfm</b>{protectiveCoverLength} Abdeckhülle für System C/X/XS <mark><b>Farbe: Stone</b></mark>",
        beosInfo: { protectiveCover: true, anzahlCalculationType: "laufMeter" },
        stkUnit: "Stk",
        mulUnit: "m",
        hasLength: true,
        callback: (params) => {
          if (params.args.chapter.type !== "sunSail") return;
          ArticleInfo.ofm.addItem(PartListHelper.getChapterArticles(params.args.chapter.articles, "sunSailList"), params.inputArgs, {}, 10 * params.article.count, {
            articleId: "00E13000012" // name: "Spannflex schwarz",
          });
          ArticleInfo.ofm.addItem(PartListHelper.getChapterArticles(params.args.chapter.articles, "sunSailList"), params.inputArgs, {}, 2 * params.article.count, {
            name: "Wasserabtropfnase",
          });
        },
      },
      "71004A002": { // Ø84
        defaultPartList: "sunSailList",
        partListNameOldApp: "<b>{length} lfm</b>{protectiveCoverLength} Abdeckhülle für System C/X/XS <mark><b>Farbe: {colorId}</b></mark>",
        beosInfo: { protectiveCover: true, anzahlCalculationType: "laufMeter" },
        stkUnit: "Stk",
        mulUnit: "lfm",
        hasLength: true,
        callback: (params) => {
          if (params.args.chapter.type !== "sunSail") return;
          ArticleInfo.ofm.addItem(PartListHelper.getChapterArticles(params.args.chapter.articles, "sunSailList"), params.inputArgs, {}, 10 * params.article.count, {
            articleId: "00E13000012" // name: "Spannflex schwarz",
          });
          ArticleInfo.ofm.addItem(PartListHelper.getChapterArticles(params.args.chapter.articles, "sunSailList"), params.inputArgs, {}, 2 * params.article.count, {
            name: "Wasserabtropfnase",
          });
        },
      },
      "71004A007": { // Ø84
        defaultPartList: "sunSailList",
        partListNameOldApp: "<b>{length} lfm</b>{protectiveCoverLength} Abdeckhülle <mark><b>Premium</b></mark> für System C/X/XS <mark><b>Farbe: Stone</b></mark>",
        beosInfo: { protectiveCover: true, anzahlCalculationType: "laufMeter" },
        stkUnit: "Stk",
        mulUnit: "m",
        hasLength: true,
        callback: (params) => {
          if (params.args.chapter.type !== "sunSail") return;
          ArticleInfo.ofm.addItem(PartListHelper.getChapterArticles(params.args.chapter.articles, "sunSailList"), params.inputArgs, {}, 2 * params.article.count, {
            name: "Wasserabtropfnase",
          });
          // OrderArticleInfo.ofm.addItem(args.sunSail.sunSailList, inputArgs, -1, "Seil 2,5mm", options.mul * 1.6, { unit: "lfm" }); // 27. September 2019
        },
      },
      "71004A008": { // Ø84
        defaultPartList: "sunSailList",
        partListNameOldApp: "<b>{length} lfm</b>{protectiveCoverLength} Abdeckhülle <mark><b>Premium</b></mark> für System C/X/XS <mark><b>Farbe: {colorId}</b></mark>",
        beosInfo: { protectiveCover: true, anzahlCalculationType: "laufMeter" },
        stkUnit: "Stk",
        mulUnit: "m",
        hasLength: true,
        callback: (params) => {
          if (params.args.chapter.type !== "sunSail") return;
          ArticleInfo.ofm.addItem(PartListHelper.getChapterArticles(params.args.chapter.articles, "sunSailList"), params.inputArgs, {}, 2 * params.article.count, {
            name: "Wasserabtropfnase",
          });
          // OrderArticleInfo.ofm.addItem(args.sunSail.sunSailList, inputArgs, -1, "Seil 2,5mm", options.mul * 1.6, { unit: "lfm" }); // 27. September 2019
        },
      },
      "71004A010": { // Ø90 // SunSailLogic.calcProtectiveCoverLengthAdd(context.ssa.getSunSailType())
        defaultPartList: "sunSailList",
        partListNameOldApp: "<b>{length} lfm</b>{protectiveCoverLength} Abdeckhülle für System C/X/XS <mark><b>Farbe: Stone</b></mark>",
        beosInfo: { protectiveCover: true, anzahlCalculationType: "laufMeter" },
        stkUnit: "Stk",
        mulUnit: "m",
        hasLength: true,
        callback: (params) => {
          if (params.args.chapter.type !== "sunSail") return;
          ArticleInfo.ofm.addItem(PartListHelper.getChapterArticles(params.args.chapter.articles, "sunSailList"), params.inputArgs, {}, 10 * params.article.count, {
            articleId: "00E13000012" // name: "Spannflex schwarz",
          });
          ArticleInfo.ofm.addItem(PartListHelper.getChapterArticles(params.args.chapter.articles, "sunSailList"), params.inputArgs, {}, 2 * params.article.count, {
            name: "Wasserabtropfnase",
          });
        },
      },
      "71004A012": { // Ø90
        defaultPartList: "sunSailList",
        partListNameOldApp: "<b>{length} lfm</b>{protectiveCoverLength} Abdeckhülle für System C/X/XS <mark><b>Farbe: {colorId}</b></mark>",
        beosInfo: { protectiveCover: true, anzahlCalculationType: "laufMeter" },
        stkUnit: "Stk",
        mulUnit: "m",
        hasLength: true,
        callback: (params) => {
          if (params.args.chapter.type !== "sunSail") return;
          ArticleInfo.ofm.addItem(PartListHelper.getChapterArticles(params.args.chapter.articles, "sunSailList"), params.inputArgs, {}, 10 * params.article.count, {
            articleId: "00E13000012" // name: "Spannflex schwarz",
          });
          ArticleInfo.ofm.addItem(PartListHelper.getChapterArticles(params.args.chapter.articles, "sunSailList"), params.inputArgs, {}, 2 * params.article.count, {
            name: "Wasserabtropfnase",
          });
        },
      },
      "71004A011": { // Ø90
        defaultPartList: "sunSailList",
        partListNameOldApp: "<b>{length} lfm</b>{protectiveCoverLength} Abdeckhülle <mark><b>Premium</b></mark> für System C/X/XS <mark><b>Farbe: Stone</b></mark>",
        beosInfo: { protectiveCover: true, anzahlCalculationType: "laufMeter" },
        stkUnit: "Stk",
        mulUnit: "m",
        hasLength: true,
        callback: (params) => {
          if (params.args.chapter.type !== "sunSail") return;
          ArticleInfo.ofm.addItem(PartListHelper.getChapterArticles(params.args.chapter.articles, "sunSailList"), params.inputArgs, {}, 2 * params.article.count, {
            name: "Wasserabtropfnase",
          });
          // OrderArticleInfo.ofm.addItem(args.sunSail.sunSailList, inputArgs, -1, "Seil 2,5mm", options.mul * 1.6, { unit: "lfm" }); // 27. September 2019
        },
      },
      "71004A013": { // Ø90
        defaultPartList: "sunSailList",
        partListNameOldApp: "<b>{length} lfm</b>{protectiveCoverLength} Abdeckhülle <mark><b>Premium</b></mark> für System C/X/XS <mark><b>Farbe: {colorId}</b></mark>",
        beosInfo: { protectiveCover: true, anzahlCalculationType: "laufMeter" },
        stkUnit: "Stk",
        mulUnit: "m",
        hasLength: true,
        callback: (params) => {
          if (params.args.chapter.type !== "sunSail") return;
          ArticleInfo.ofm.addItem(PartListHelper.getChapterArticles(params.args.chapter.articles, "sunSailList"), params.inputArgs, {}, 2 * params.article.count, {
            name: "Wasserabtropfnase",
          });
          // OrderArticleInfo.ofm.addItem(args.sunSail.sunSailList, inputArgs, -1, "Seil 2,5mm", options.mul * 1.6, { unit: "lfm" }); // 27. September 2019
        },
      },
      "00E13001430": {
        defaultPartList: "sunSailList",
      },
      // Schutzabdeckung für Sonnensegelecken
      "71004A003": {
        defaultPartList: "sunSailList",
        partListName: "Plattenabdeckung Schutzhülle 4-Eck <mark><b>Farbe: Stone</b></mark>",
      },
      "71004A004": {
        defaultPartList: "sunSailList",
        partListName: "Plattenabdeckung Schutzhülle 4-Eck <mark><b>Farbe: {colorId}</b></mark>",
      },

      // Schutzhülle
      "73004A001": {
        defaultPartList: "sunSailList",
        partListNameOldApp: "<b>{length} lfm</b>{protectiveCoverLength} Abdeckhülle für System A/M/CS <mark><b>Farbe: Stone</b></mark>",
        beosInfo: { protectiveCover: true, anzahlCalculationType: "laufMeter" },
        stkUnit: "Stk",
        mulUnit: "m",
        hasLength: true,
        callback: (params) => {
          if (params.args.chapter.type !== "sunSail") return;
          ArticleInfo.ofm.addItem(PartListHelper.getChapterArticles(params.args.chapter.articles, "sunSailList"), params.inputArgs, {}, 10 * params.article.count, {
            articleId: "00E13000012" // name: "Spannflex schwarz",
          });
        },
      },
      "73004A002": {
        defaultPartList: "sunSailList",
        partListNameOldApp: "<b>{length} lfm</b>{protectiveCoverLength} Abdeckhülle für System A/M/CS <mark><b>Farbe: {colorId}</b></mark>",
        beosInfo: { protectiveCover: true, anzahlCalculationType: "laufMeter" },
        stkUnit: "Stk",
        mulUnit: "m",
        hasLength: true,
        callback: (params) => {
          if (params.args.chapter.type !== "sunSail") return;
          ArticleInfo.ofm.addItem(PartListHelper.getChapterArticles(params.args.chapter.articles, "sunSailList"), params.inputArgs, {}, 10 * params.article.count, {
            articleId: "00E13000012" // name: "Spannflex schwarz",
          });
        },
      },
      "70004A007": {
        defaultPartList: "sunSailList",
        partListNameOldApp: "<b>{length} lfm</b>{protectiveCoverLength} Abdeckhülle <mark><b>Premium</b></mark> für System A/M/CS <mark><b>Farbe: Stone</b></mark>",
        beosInfo: { protectiveCover: true, anzahlCalculationType: "laufMeter" },
        stkUnit: "Stk",
        mulUnit: "m",
        hasLength: true,
      },
      "70004A008": {
        defaultPartList: "sunSailList",
        partListNameOldApp: "<b>{length} lfm</b>{protectiveCoverLength} Abdeckhülle <mark><b>Premium</b></mark> für System A/M/CS <mark><b>Farbe: {colorId}</b></mark>",
        beosInfo: { protectiveCover: true, anzahlCalculationType: "laufMeter" },
        stkUnit: "Stk",
        mulUnit: "m",
        hasLength: true,
      },
      // Hischmannstecker (Buchse)
      "00E13000696": { shoppingCartPriority: ShoppingCartPriority.Motor },
      // Winkelleiste Alu elox. für Raff & Outdoorvorhang seilt. Montage 60x30
      "00E13001419": {
        stkUnit: "Stk",
        mulUnit: "m",
        hasLength: true,
        partListName: "Winkelleiste Alu elox. inkl. Befestigungsmaterial der Führungsschiene: <b>{mul1000}mm</b>",
        beosInfo: { anzahlCalculationType: "laufMeter" }
      },
      // "Seilzugsystem" extra unter Seilzug aufgelistet... ?
      "00E13001371": { defaultPartList: "none" },
      "00E13000206": {
        maxUnits: 2,
      },
      "00E13000227": {
        // defaultPartList: "colliDispatch",
        // colli: { diag: 22, length: 1.4, weight: 14, place: 34 },
        // partListName: "SF-1400 (Schraubfundament <mark><b>KSF E 140x1400 mm</b></mark>)",
        defaultPartList: "hardwareList",
        orderGroup: "Vorgefertigt",
      },
      "00E13000228": {
        // defaultPartList: "colliDispatch",
        // colli: { diag: 22, length: 1.6, weight: 19, place: 33 },
        // partListName: "SF-1600 (Schraubfundament <mark><b>KSF E 140x1600 mm</b></mark>)",
        defaultPartList: "hardwareList",
        orderGroup: "Vorgefertigt",
      },
      "00E13000320": {
        maxUnits: 2,
        callback: (params) => {
          ArticleInfo.ofm.addItem(PartListHelper.getChapterArticles(params.args.chapter.articles, params.partListName), params.inputArgs, params.article.affiliation, params.article.count, {
            articleId: "00E13000206",
          });
        },
      },
      "00E13000321": {
        maxUnits: 2,
        callback: (params) => {
          ArticleInfo.ofm.addItem(PartListHelper.getChapterArticles(params.args.chapter.articles, params.partListName), params.inputArgs, params.article.affiliation, params.article.count, {
            articleId: "00E13000206",
          });
        },
      },
      "00E13000322": {
        maxUnits: 2,
        callback: (params) => {
          ArticleInfo.ofm.addItem(PartListHelper.getChapterArticles(params.args.chapter.articles, params.partListName), params.inputArgs, params.article.affiliation, params.article.count, {
            articleId: "00E13000206",
          });
        },
      },
      "00E13000304": {
        defaultPartList: "hardwareList" /*, name: "Basecube Abdeckung 5 tlk Holz Esche"*/,
        callback: (params) => {
          if (params.args.chapter.type !== "sunSail") return;
          const list = "stockList"; // https://plaspack.atlassian.net/browse/APP-1277
          ArticleInfo.ofm.addItem(PartListHelper.getChapterArticles(params.args.chapter.articles, list), params.inputArgs, params.article.affiliation, params.article.count * 16, {
            name: "Befestigungswinkel für Holz bei Base Cube + Spax 4x30",
          });
        },
      },
      // "Wandschelle ø78"
      "60002A002": { /*partListName: "Abdeckkappe f. 78mm für Alu-Masten grau",*/ coveringCap: { name: "Abdeckkappe Design (Edelstahl Masten)" } },
      "60002A000": { /*partListName: "Abdeckkappe Design (Edelstahl Masten)",*/ coveringCap: { name: "Abdeckkappe f. DM 70x3 für Niro-Masten schwarz" } },
      "00E13000216": { /*partListName: "Abdeckkappe Design (Edelstahl Masten)",*/ coveringCap: { name: "Abdeckkappe Design (Edelstahl Masten)" } },
      "00E13000217": { /*partListName: "Abdeckkappe Design (Alu)",*/ coveringCap: { name: "Abdeckkappe Design (Alu)" } },
      "00E13001689": { /*partListName: "Abdeckkappe Design (Alu black)",*/  coveringCap: { name: "Abdeckkappe Design (Alu black)" } },
      "00E13000243": { maxUnits: 3 },
      "00E13001864": { maxUnits: 3 },
      "00E13000110": { maxUnits: 3 },
      "00E13001863": { maxUnits: 3 },
      "00E13000245": {
        maxUnits: 3,
      },
      "00E13000249": {
        maxUnits: 2,
      },
      "00E13000246": {
        maxUnits: 2,
      },
      "00E13000247": {
        maxUnits: 2,
      },
      "00E13000143": { maxUnits: 3 },
      "00E13001860": { maxUnits: 3 },
      "00E13000120": {
        // partListName: "Abstandhalter Standard Wand/Mast 5cm für <b>Edelstahl</b> Mast (ø70) inklusive Wandschelle und Formrohr",
        maxUnits: 2,
      },
      "00E13000248": { maxUnits: 2 },
      "00E13000121": { maxUnits: 2 },
      "00E13000122": { maxUnits: 2 },

      // Mast-Adapter Rundschelle für Edelstahl Mast
      "00E13000103": { maxUnits: 3 },
      "00E13001734": { maxUnits: 3 }, // Design Mastschelle
      "00E13001779": { defaultPartList: "none", maxUnits: 1, partListName: "Design Rundschelle Edelstahl Mast ø70mm" }, // AufpreisDesign Mastschelle
      // Mast-Adapter Rundschelle für Aluminium Mast
      "00E13000219": { maxUnits: 3 },
      "00E13001735": { maxUnits: 3 }, // Design Mastschelle
      "00E13001780": { defaultPartList: "none", maxUnits: 1, partListName: "Design Rundschelle Aluminium Mast ø78mm" }, // AufpreisDesign Mastschelle

      // *** WandPlatten ****
      "00E13001095": { partListName: "Wandplatte f. A&M 3-Eck 150/156/6 LH innen = 100" },

      // Aufpreis Snap Design
      "00E13001322": {
        partListName: "{articleRef:00E13000591}",
      },
      // Aufpreis Snap Premium
      "00E13001323": {
        partListName: "{articleRef:00E13001367}",
      },
      // Winsch mit Kurbel für Soliday A
      "00E13000059": {
        // partListName: "{as400Text} f. {poleMaterialName}",
        // additionalArticleInfo: {
        //   material: (article, context) => {
        //     return article.variants?.poleMaterial && AdditionalArticleInfo.material(context, article.variants.poleMaterial)
        //   }
        // },
      },
      "00E13000743": {
        partListName: "{as400Text} f. {poleMaterialName}",
      },
      // Snap-Grundplatte mit Fallstoppklemme und Bügelschloss
      "00E13001631": {
        partListName: "{as400Text} f. {poleMaterialName}",
      },
      // Snap-Grundplatte mit Fallstoppklemme und Bügelschloss (Aufpreis für X/XS)
      "00E13001684": { defaultPartList: "none" },
      // kommt aus App (Warenkorb 3.1)

      "00E13000062": {
        defaultPartList: "sunSailList",
        maxUnits: 50,
        stkUnit: "Stk",
        mulUnit: "m",
        rope: { ropeType: "4", diameter: 4, colorId: "grey" },
      },

      // spannseil 6.5 grau E (enthalten)
      "00E13001406": {
        defaultPartList: "sunSailList",
        stkUnit: "", // m => shows 1m x 0€ in App
        mulUnit: "", // m => shows 1m in App Rechnung
        rope: { ropeType: "6.5E", diameter: 6.5, colorId: "grey" },
      },
      // spannseil 6.5 Hellblau E
      "00E13001392": {
        defaultPartList: "sunSailList",
        stkUnit: "", // m => shows 1m x 0€ in App
        mulUnit: "", // m => shows 1m in App Rechnung
        rope: { ropeType: "6.5E", diameter: 6.5, colorId: "blue" },
      },
      // spannseil 6.5 navy E
      "00E13001393": {
        defaultPartList: "sunSailList",
        stkUnit: "", // m => shows 1m x 0€ in App
        mulUnit: "", // m => shows 1m in App Rechnung
        rope: { ropeType: "6.5E", diameter: 6.5, colorId: "navy" },
      },
      // spannseil 6.5 purple E
      "00E13001394": {
        defaultPartList: "sunSailList",
        stkUnit: "", // m => shows 1m x 0€ in App
        mulUnit: "", // m => shows 1m in App Rechnung
        rope: { ropeType: "6.5E", diameter: 6.5, colorId: "purple" },
      },
      // spannseil 6.5 orange E
      "00E13001395": {
        defaultPartList: "sunSailList",
        stkUnit: "", // m => shows 1m x 0€ in App
        mulUnit: "", // m => shows 1m in App Rechnung
        rope: { ropeType: "6.5E", diameter: 6.5, colorId: "orange" },
      },
      // spannseil 6.5 red E
      "00E13001396": {
        defaultPartList: "sunSailList",
        stkUnit: "", // m => shows 1m x 0€ in App
        mulUnit: "", // m => shows 1m in App Rechnung
        rope: { ropeType: "6.5E", diameter: 6.5, colorId: "red" },
      },
      // spannseil 6.5 Grün E
      "00E13001397": {
        defaultPartList: "sunSailList",
        stkUnit: "", // m => shows 1m x 0€ in App
        mulUnit: "", // m => shows 1m in App Rechnung
        rope: { ropeType: "6.5E", diameter: 6.5, colorId: "green" },
      },
      // spannseil 6.5 schwarz/orange
      "00E13002071": {
        defaultPartList: "sunSailList",
        stkUnit: "", // m => shows 1m x 0€ in App
        mulUnit: "", // m => shows 1m in App Rechnung
        rope: { ropeType: "6.5E", diameter: 6.5, colorId: "black" },
      },

      // spannseil 8.0 grau (Aufpreis)
      "00E13001407": {
        defaultPartList: "sunSailList",
        stkUnit: "", // m => shows 1m x 0€ in App
        mulUnit: "", // m => shows 1m in App Rechnung
        rope: { ropeType: "8A", diameter: 8, colorId: "grey" },
      },
      // spannseil 8.0 Hellblau A
      "00E13001360": {
        defaultPartList: "sunSailList",
        stkUnit: "", // m => shows 1m x 0€ in App
        mulUnit: "", // m => shows 1m in App Rechnung
        rope: { ropeType: "8A", diameter: 8, colorId: "blue" },
      },
      // spannseil 8.0 Dunkelblau A
      "00E13001361": {
        defaultPartList: "sunSailList",
        stkUnit: "", // m => shows 1m x 0€ in App
        mulUnit: "", // m => shows 1m in App Rechnung
        rope: { ropeType: "8A", diameter: 8, colorId: "navy" },
      },
      // spannseil 8.0 Violett A
      "00E13001362": {
        defaultPartList: "sunSailList",
        stkUnit: "", // m => shows 1m x 0€ in App
        mulUnit: "", // m => shows 1m in App Rechnung
        rope: { ropeType: "8A", diameter: 8, colorId: "purple" },
      },
      // spannseil 8.0 Orange A
      "00E13001363": {
        defaultPartList: "sunSailList",
        stkUnit: "", // m => shows 1m x 0€ in App
        mulUnit: "", // m => shows 1m in App Rechnung
        rope: { ropeType: "8A", diameter: 8, colorId: "orange" },
      },
      // spannseil 8.0 Rot A
      "00E13001364": {
        defaultPartList: "sunSailList",
        stkUnit: "", // m => shows 1m x 0€ in App
        mulUnit: "", // m => shows 1m in App Rechnung
        rope: { ropeType: "8A", diameter: 8, colorId: "red" },
      },
      // spannseil 8.0 Grün A
      "00E13001365": {
        defaultPartList: "sunSailList",
        stkUnit: "", // m => shows 1m x 0€ in App
        mulUnit: "", // m => shows 1m in App Rechnung
        rope: { ropeType: "8A", diameter: 8, colorId: "green" },
      },
      // spannseil 8.0 schwarz/orange
      "00E13002073": {
        defaultPartList: "sunSailList",
        stkUnit: "", // m => shows 1m x 0€ in App
        mulUnit: "", // m => shows 1m in App Rechnung
        rope: { ropeType: "8A", diameter: 8, colorId: "black" },
      },

      //  Höhenverstellung C/CS/CS-Twin
      // ohne Funk
      "00E13001058": ArticleInfo.getElectricHeightChange("e12r40", true), // natur
      "00E13001974": ArticleInfo.getElectricHeightChange("e12r40", true), // anthrazit
      // mit Funk
      "00E13000976": ArticleInfo.getElectricHeightChange("c12r40", true), // natur
      "00E13001973": ArticleInfo.getElectricHeightChange("c12r40", true), // anthrazit
      "00E13001925": ArticleInfo.getElectricHeightChange("c12r40+", false),
      "00E13001926": ArticleInfo.getElectricHeightChange("c12r40+", false),
      "00E13001927": ArticleInfo.getElectricHeightChange("c12r40+", true), // natur
      "00E13001975": ArticleInfo.getElectricHeightChange("c12r40+", true), // anthrazit
      "00E13000988": {
        partListName: "Manuelles Höhenverstellungsset Design <mark><b>(Schwarz)</b></mark> inkl. Kurbel für Droppole",
      }, // deprecated 10.4.2020
      "71006A001": {
        partListName: "Manuelles Höhenverstellungsset ({poleMaterialName}) inkl. Rundschelle",
      },

      // spannseil 6.5 grau
      "00E13000023": {
        defaultPartList: "sunSailList",
        maxUnits: 50,
        stkUnit: "Stk",
        mulUnit: "m",
        rope: { ropeType: "6.5", diameter: 6.5, colorId: "grey" },
      },
      // spannseil 6.5 Hellblau
      "00E13001346": {
        defaultPartList: "sunSailList",
        maxUnits: 50,
        stkUnit: "Stk",
        mulUnit: "m",
        rope: { ropeType: "6.5", diameter: 6.5, colorId: "blue" },
      },
      // spannseil 6.5 Dunkelblau
      "00E13001347": {
        defaultPartList: "sunSailList",
        maxUnits: 50,
        stkUnit: "Stk",
        mulUnit: "m",
        rope: { ropeType: "6.5", diameter: 6.5, colorId: "orange" },
      },
      // spannseil 6.5 Violett
      "00E13001348": {
        defaultPartList: "sunSailList",
        maxUnits: 50,
        stkUnit: "Stk",
        mulUnit: "m",
        rope: { ropeType: "6.5", diameter: 6.5, colorId: "red" },
      },
      // spannseil 6.5 Orange
      "00E13001349": {
        defaultPartList: "sunSailList",
        maxUnits: 50,
        stkUnit: "Stk",
        mulUnit: "m",
        rope: { ropeType: "6.5", diameter: 6.5, colorId: "purple" },
      },
      // spannseil 6.5 Rot
      "00E13001350": {
        defaultPartList: "sunSailList",
        maxUnits: 50,
        stkUnit: "Stk",
        mulUnit: "m",
        rope: { ropeType: "6.5", diameter: 6.5, colorId: "navy" },
      },
      // spannseil 6.5 Grün
      "00E13001351": {
        defaultPartList: "sunSailList",
        maxUnits: 50,
        stkUnit: "Stk",
        mulUnit: "m",
        rope: { ropeType: "6.5", diameter: 6.5, colorId: "green" },
      },
      // spannseil 6.5 schwarz/orange
      "00E13002048": {
        defaultPartList: "sunSailList",
        maxUnits: 50,
        stkUnit: "Stk",
        mulUnit: "m",
        rope: { ropeType: "6.5", diameter: 6.5, colorId: "black" },
      },

      // spannseil 8.0 grau
      "00E13001359": {
        defaultPartList: "sunSailList",
        maxUnits: 50,
        stkUnit: "Stk",
        mulUnit: "m",
        rope: { ropeType: "8", diameter: 8, colorId: "grey" },
      },
      // spannseil 8.0 Hellblau
      "00E13001352": {
        defaultPartList: "sunSailList",
        maxUnits: 50,
        stkUnit: "Stk",
        mulUnit: "m",
        rope: { ropeType: "8", diameter: 8, colorId: "blue" },
      },
      // spannseil 8.0 Dunkelblau
      "00E13001353": {
        defaultPartList: "sunSailList",
        maxUnits: 50,
        stkUnit: "Stk",
        mulUnit: "m",
        rope: { ropeType: "8", diameter: 8, colorId: "orange" },
      },
      // spannseil 8.0 Violett
      "00E13001354": {
        defaultPartList: "sunSailList",
        maxUnits: 50,
        stkUnit: "Stk",
        mulUnit: "m",
        rope: { ropeType: "8", diameter: 8, colorId: "red" },
      },
      // spannseil 8.0 Orange
      "00E13001355": {
        defaultPartList: "sunSailList",
        maxUnits: 50,
        stkUnit: "Stk",
        mulUnit: "m",
        rope: { ropeType: "8", diameter: 8, colorId: "purple" },
      },
      // spannseil 8.0 Rot
      "00E13001356": {
        defaultPartList: "sunSailList",
        maxUnits: 50,
        stkUnit: "Stk",
        mulUnit: "m",
        rope: { ropeType: "8", diameter: 8, colorId: "navy" },
      },
      // spannseil 8.0 Grün
      "00E13001357": {
        defaultPartList: "sunSailList",
        maxUnits: 50,
        stkUnit: "Stk",
        mulUnit: "m",
        rope: { ropeType: "8", diameter: 8, colorId: "green" },
      },
      // spannseil 8 schwarz/orange
      "00E13002063": {
        defaultPartList: "sunSailList",
        maxUnits: 50,
        stkUnit: "Stk",
        mulUnit: "m",
        rope: { ropeType: "8", diameter: 8, colorId: "black" },
      },
      // "Profispannseil Ø10mm"
      "00E13000083": {
        defaultPartList: "sunSailList",
        maxUnits: 50,
        stkUnit: "Stk",
        mulUnit: "m",
        rope: { ropeType: "10", diameter: 10, colorId: "grey" },
      },
      "00E13000833": { partListName: "Gewindebolzen M10x140 + Mutter und Ringmutter verzinkt" },
      "00E13000093": { mulUnit: "m" },
      "00E13000784": { mulUnit: "m" },
      "00E13000785": { mulUnit: "m" },
      "00E13000786": { mulUnit: "m" },
      "00E13000787": { mulUnit: "m" },

      [RAFF_PREMONTAGE]: { defaultPartList: "sunSailList", mulUnit: "m2", shoppingCartPriority: ShoppingCartPriority.PreMontage },
    };
  }

  static init(ofm: IOrderFormManager): void {
    // may be removed in the future, since OrderFormManager moved to common...
    this.ofm = ofm;
  }

  private static createCompleteArticles(): Record<string, IOrderArticleInfo> {
    const articles = this.createArticles();
    const keys = Object.keys(articles);
    const result: Record<string, IOrderArticleInfo> = {};
    for (const k of keys) {
      const article = articles[k];
      result[k] = this.enrich(k, article);
    }
    return result;
  }

  static calcAnnotationArticles<K extends keyof IOrderArticleInfo>(property: K): RequireOne<IOrderArticleInfo, K>[] {
    const articles = this.getArticles();
    const keys = Object.keys(articles);
    const ret: RequireOne<IOrderArticleInfo, K>[] = [];
    for (const k of keys) {
      const a = articles[k];
      if (a[property]) {
        ret.push(a as any);
      }
    }
    return ret;
  }

  private static getArticles(): Record<string, IOrderArticleInfo> {
    if (!this.articles) this.articles = ArticleInfo.createCompleteArticles();
    return this.articles;
  }

  static getCSWallPlateArticles(): string[] {
    return ["00E13000046", "00E13001862"];
  }

  static getCoveringCapArticles(): AnnotationArticle<"coveringCap">[] {
    // DataHelper.hasArticle(articles, poleIndex, "00E13000216") || // Niro
    // DataHelper.hasArticle(articles, poleIndex, "00E13000217") || // Alu
    // DataHelper.hasArticle(articles, poleIndex, "00E13001689") // Alu schwarz

    if (!this.coveringCapArticles)
      this.coveringCapArticles = ArticleInfo.calcAnnotationArticles("coveringCap");
    return this.coveringCapArticles;
  }

  // static findWinchInfoById(id: string): IOrderArticleInfo | undefined {
  //   const article = this.getArticles()[id];
  //   if (article.winch) return article;
  //   return undefined;
  // }

  private static enrich(id: string, info: Partial<IOrderArticleInfo>): IOrderArticleInfo {
    return {
      id,
      type: "standard",
      defaultPartList: "stockList",
      maxUnits: 1,
      ...info,
    };
  }

  static getArticleInfoForShoppingArticle(item: IShoppingArticle): IOrderArticleInfo | undefined {
    const articleInfo = ArticleInfo.getArticleInfo(item.articleNumber);
    const ai: IOrderArticleInfo = {
      id: "",
      defaultPartList: "stockList",
      type: "standard",
      partListName: item.partList?.name,
      maxUnits: 1,
      ...(articleInfo ? articleInfo : {}),
    }

    if (item.partList?.priority !== undefined)
      ai.shoppingCartPriority = item.partList?.priority;
    if (item.partList?.name)
      ai.partListName = item.partList?.name;

    if (item.partList?.list) {
      const defaultPartList = getValueOrFunction(ai.defaultPartList, undefined);
      if (defaultPartList !== "none" && defaultPartList !== "handledOutside")
        ai.defaultPartList = item.partList.list;
    }
    return ai;
  }

  static getArticleInfo(articleNumber: string): IOrderArticleInfo | undefined {
    const ai = this.getArticles()[articleNumber];
    if (!ai && ArticleLogic.isClothArticle(articleNumber)) {
      return this.enrich(articleNumber, {
        defaultPartList: "sunSailList",
        type: "standard",
        maxUnits: 1,
        // mulUnit: "m2",
      });
    }
    return ai;
  }

  static getMotorName(remote: boolean): string {
    if (remote) return "Funkmotor C12";
    else return "Motor ohne Funk E12";
  }

  static getHeightChangeMotorArticle(type: MotorType): string {
    switch (type) {
      case "c12r40": return "Funkmotor <strong>(R40) C12</strong> für elektr. Höhenverstellung";
      case "e12r40": return "Motor ohne Funk <strong>(R40) E12</strong> für elektr. Höhenverstellung";
      case "c12r40+": return "Funkmotor <strong>R40-17-C12+</strong> mit Funk bidirektional für elektr. Höhenverstellung";
    }
    return "Unknown";
  }

  /** @deprecated use beosuser or beoscompany language instead */
  static getLanguageFromCountryCode(countryCode: string): string {
    switch (countryCode.toLocaleUpperCase()) {
      case "A":
      case "D":
      case "CH":
        return "Deutsch";
      case "I":
        return "Italienisch";
      case "E":
        return "Spanisch";
    }
    return "Englisch";
  }

  static getElectricHeightChange(motorType: MotorType, isDroppole: boolean): Partial<IOrderArticleInfo> {
    return {
      callback: (params) => {
        if (isDroppole) {
          ArticleInfo.ofm.addItem(PartListHelper.getChapterArticles(params.args.chapter.articles, "stockList"), params.inputArgs, params.article.affiliation, params.article.count, {
            name: ArticleInfo.getHeightChangeMotorArticle(motorType),
          });
        }
      },
    }
  }

  static calcUnits(id: string, units: { stkUnit?: Unit, mulUnit?: Unit } | undefined): IArticleUnits {
    // if (id === "00E13001129") {
    //   console.log("HJERE!")
    // }
    const info = ArticleInfo.getArticleInfo(id);
    const stkUnit = units?.stkUnit ?? info?.stkUnit ?? "Stk";
    const mulUnit = units?.mulUnit ?? info?.mulUnit ?? "";
    return {
      stkUnit,
      mulUnit
    }
  }
}

export interface IArticleUnits {
  stkUnit: Unit;
  mulUnit: Unit;
}

